/**
 * Web → Native transport for the embedded Timeline bridge.
 *
 * Messages accumulate in an outbox array. A single setTimeout(0) fires ONE
 * window.location.href ping. Native catches the ping, then PULLS batches
 * from the outbox via EvaluateJavaScript("window.__drainOutbox()") in a
 * loop until empty. Each __drainOutbox call RETURNS the batch payload as
 * a string (no navigation), so there are no URL size limits and no dropped
 * messages from multiple synchronous window.location.href assignments.
 *
 * Split out of embeddedBridge.ts so feature modules can send to native
 * without importing the (large) inbound-dispatch surface.
 */

import { capabilities } from '@/capabilities.ts';

// ─── Outbox transport ────────────────────────────────────────────────────

const outbox: string[] = [];
let pingScheduled = false;

// 20260709 - NAVIGATION IN-FLIGHT GUARD. A previspro:// navigation that native has not yet
// intercepted is silently CANCELLED by a subsequent location.href assignment — the WKWebView drop
// bug this file exists to work around. The previous code still raced itself: __drainOutboxNavigate
// delivered a batch by navigation, and any message enqueued within the next tick fired a __ping__
// navigation right behind it, cancelling the batch. Observed in the wild: the loader's batch
// (diagLogs + projectLoaded) was lost when the dossier pipeline's wave-1 picks enqueued at board
// mount — Unity never received projectLoaded, so the board fill drain and the initial
// sceneReady/allScenesReady pushes wedged forever. Every navigation now records itself in-flight,
// and pings DEFER while one is pending. Cleared two ways: native evaluates __drainOutboxNavigate()
// in response to ANY bridge navigation (its entry proves the prior navigation was consumed — the
// timeline panel acks every batch, not just pings), plus a timeout fallback for panels that only
// ack pings / a genuinely lost navigation.
// 20260908 - every embedded host now acks every processed navigation (the ack was added to the
// purchasing, Previs Assist, sign-in, avatar-create, credit-pack, screenplay-manager, script-lining,
// avatar-customizer and canvas-screenplay hosts), so the 800 ms timeout below is a backstop for a
// genuinely lost navigation only — no longer the normal path for any panel.
let navInFlight = false;
let navInFlightSince = 0;
const NAV_IN_FLIGHT_TIMEOUT_MS = 800;
const PING_RETRY_MS = 50;

// 20260801 - W1 PERF (temporary, module-local so nothing outside transport depends on it).
// viewReady is the message Unity waits on to reveal the board, and the outbox path between
// enqueue and drain is one of the candidates for the wall. Stamped on the way in, reported once
// on the drain that carries it out. Delete this block plus its four touch points below with the
// rest of src/perf/.
const viewReadyEgress = { enqueuedAt: -1, pingAt: -1, retries: 0, navInFlight: false, logged: true };

// 20260908 - HELP-OPEN TIMING PROBE (tag "HelpTrace"; delete as one sweep with
// Assets/Scripts/Popups/HelpOpenTrace.cs and its call sites). Same four touch points and the same
// shape as viewReadyEgress above — deliberately a SECOND record rather than a generalisation of
// that one, because viewReady and openHelp can be in the outbox at the same time and one record
// would have them overwrite each other's stamps.
const openHelpEgress = { enqueuedAt: -1, pingAt: -1, retries: 0, navInFlight: false, logged: true };

function navigate(url: string): void {
  // 20260919 - SURVIVE JSDOM TEARDOWN. Every call here arrives on a timer (schedulePing's
  // setTimeout(0), tryPing's retry, or native's drain), so the last ping a test file enqueues can
  // fire AFTER vitest has torn the environment down, where `window` no longer exists. The
  // ReferenceError escapes as an unhandled error and fails the whole RUN — "1 error" with every
  // test passing — which is indistinguishable from a real flake and only shows up under load,
  // when teardown and the pending timer interleave. There is nothing left to navigate to at that
  // point, so drop it. Checked BEFORE navInFlight is set, so the retry ladder does not re-arm.
  if (typeof window === 'undefined') return;
  navInFlight = true;
  navInFlightSince = Date.now();
  window.location.href = url;
}

function tryPing(): void {
  pingScheduled = false;
  if (outbox.length === 0) return;
  if (navInFlight && Date.now() - navInFlightSince < NAV_IN_FLIGHT_TIMEOUT_MS) {
    // A navigation is still in flight — another location.href now could cancel it. Retry shortly;
    // native's next __drainOutboxNavigate() also clears the flag and pumps the queue directly.
    pingScheduled = true;
    if (!viewReadyEgress.logged) viewReadyEgress.retries += 1;
    if (!openHelpEgress.logged) openHelpEgress.retries += 1;
    setTimeout(tryPing, PING_RETRY_MS);
    return;
  }
  if (!viewReadyEgress.logged && viewReadyEgress.pingAt < 0) viewReadyEgress.pingAt = performance.now();
  if (!openHelpEgress.logged && openHelpEgress.pingAt < 0) openHelpEgress.pingAt = performance.now();
  navigate('previspro://bridge?msg=__ping__');
}

function schedulePing(): void {
  if (pingScheduled) return;
  pingScheduled = true;
  setTimeout(tryPing, 0);
}

export function sendToNative(action: string, payload: Record<string, unknown>): void {
  // Debug-mode intercept: DebugPage flow simulations set this to capture outgoing messages
  const debugIntercept = (window as unknown as Record<string, unknown>).__debugSendIntercept as
    ((action: string, payload: Record<string, unknown>) => void) | undefined;
  if (debugIntercept) { debugIntercept(action, payload); return; }

  // No-op outside embedded WebView — prevents Safari "invalid address" errors
  if (!capabilities.bridge) return;

  const msg = JSON.stringify({ action, ...payload });

  // Force-sync mode: stash messages for combined bundle instead of navigating directly.
  // __collectAndSendForceFlush() sends everything in one navigation to avoid WKWebView's
  // silent-drop bug where multiple synchronous location.href assignments lose all but the last.
  if ((window as unknown as Record<string, unknown>).__previsForceSync) {
    const win = window as unknown as Record<string, unknown>;
    if (!Array.isArray(win.__pendingForceFlushMessages)) {
      win.__pendingForceFlushMessages = [];
    }
    (win.__pendingForceFlushMessages as string[]).push(msg);
    return;
  }

  if (action === 'viewReady') {
    viewReadyEgress.enqueuedAt = performance.now();
    viewReadyEgress.pingAt = -1;
    viewReadyEgress.retries = 0;
    viewReadyEgress.navInFlight = navInFlight;
    viewReadyEgress.logged = false;
  }

  // 20260908 - HELP-OPEN TIMING PROBE: openHelp's own egress stamps (see openHelpEgress above).
  if (action === 'openHelp') {
    openHelpEgress.enqueuedAt = performance.now();
    openHelpEgress.pingAt = -1;
    openHelpEgress.retries = 0;
    openHelpEgress.navInFlight = navInFlight;
    openHelpEgress.logged = false;
  }

  outbox.push(msg);
  schedulePing();
}

// Backwards-compat alias — all existing callers (sync bridges, AnnotationCanvas) work unchanged
export const enqueueSendToNative = sendToNative;

/**
 * Deliver ONE message by its own navigation, honouring the in-flight guard above.
 *
 * For the boot signal (`ready`), which cannot ride the outbox: it is the message that tells native
 * the page exists, and the panels treat it as the start of the session.
 *
 * 20260824 - This exists because embeddedBridge.ts used to send `ready` with a BARE
 * `window.location.href = …`, outside the outbox and outside the guard. That is the one thing this
 * module documents as unsafe (see the NAVIGATION IN-FLIGHT GUARD note at the top): a previspro://
 * navigation native has not yet intercepted is silently cancelled by the next location.href
 * assignment. Because the bare send never recorded itself as in-flight, a message enqueued moments
 * earlier would fire its `setTimeout(0)` ping straight over the top of `ready` and destroy it —
 * and losing `ready` specifically is fatal rather than lossy: Unity gates PushProjectToWebView on
 * it, so no setSqlitePath is ever sent, no projectLoaded ever comes back, and the board sits empty
 * behind the reveal timeout. Observed on device 20260824 the moment a launch-trace diagLog was
 * added at SceneBoardPage mount, which is simply the first thing that ever enqueued that early;
 * the hazard predates it and any future early send would have found it.
 *
 * Two halves, both required: WAIT if a navigation is already in flight (do not clobber someone
 * else), and REGISTER this one (so the next ping defers behind it rather than clobbering us).
 */
export function sendDirectNavigation(action: string, payload: Record<string, unknown> = {}): void {
  if (!capabilities.bridge) return;

  // Debug-mode intercept, the same one sendToNative honours — without it this function was the ONE
  // hole in the harness's fake-Unity containment: the debug page can force `capabilities.bridge`
  // true (src/debug/sceneBoardFakeUnity.ts) to reach the bridge-gated board surfaces, and initBridge
  // then fires `ready` through here as a real previspro:// navigation, which in a browser is a
  // failed load rather than a message.
  //
  // ORDER MATTERS, and it is the opposite of sendToNative's. Here the intercept is checked AFTER the
  // capabilities gate, not before: jsdom leaves `capabilities.bridge` false, so every
  // installFakeUnity suite returns at the line above and `ready` never joins its recorded outbound
  // list — which applyReimportSizeGuard.test.ts's `expect(fx.outbound).toHaveLength(0)` depends on.
  const debugIntercept = (window as unknown as Record<string, unknown>).__debugSendIntercept as
    ((action: string, payload: Record<string, unknown>) => void) | undefined;
  if (debugIntercept) { debugIntercept(action, payload); return; }

  const url = `previspro://bridge?msg=${encodeURIComponent(JSON.stringify({ action, ...payload }))}`;

  const attempt = (): void => {
    if (navInFlight && Date.now() - navInFlightSince < NAV_IN_FLIGHT_TIMEOUT_MS) {
      setTimeout(attempt, PING_RETRY_MS);
      return;
    }
    navigate(url);
  };

  attempt();
}

// Internal: splices one batch from the outbox and returns it as a string.
// Called by __drainOutboxNavigate() — not called directly by native.
// Exported so a sender can pre-check its own payload against the SAME number rather than keeping a
// second copy of it (features/screenplay.ts's applyReimport guard). Nothing about the batching
// behaviour below changes: this limit still applies only to AGGREGATION — the single-message fast
// path returns before it, and the loop always takes the first message whatever its size.
//
// ⚠️ ONE CONSTANT, TWO UNITS — known gap, fix ticketed (it is a change to the accounting below,
// not to the guard). The batcher compares RAW JSON lengths (`outbox[i].length` at :131) while the
// guard compares the ENCODED URL length, and percent-encoding expands JSON by ~1.53x (measured).
// So the batcher under-counts what it will actually put on the wire: a payload the guard passed at
// just under the ceiling can still cross it once a co-queued message is appended and the whole
// batch is encoded. Until the batcher measures encoded bytes too, treat this number as an
// approximate ceiling for aggregation and an exact one only for the single-message guard.
export const MAX_BATCH_BYTES = 1_500_000; // stay under WKWebView URL length limit

(window as unknown as Record<string, unknown>).__drainOutbox = (): string => {
  if (outbox.length === 0) return '';

  // Fast path: single message
  if (outbox.length === 1) {
    return outbox.splice(0, 1)[0];
  }

  // Take messages from the front of the outbox up to the byte limit
  let batchSize = 0;
  let batchEnd = 0;
  for (let i = 0; i < outbox.length; i++) {
    const msgSize = outbox[i].length;
    if (batchEnd > 0 && batchSize + msgSize > MAX_BATCH_BYTES) break;
    batchSize += msgSize;
    batchEnd = i + 1;
  }

  const batch = outbox.splice(0, batchEnd);
  return batch.length === 1 ? batch[0] : '__batch__\n' + batch.join('\n');
};

// Called by native via EvaluateJavaScript("window.__drainOutboxNavigate()") — in response to a
// __ping__, and (timeline panel) as an ACK after processing ANY bridge navigation. Its entry proves
// the previous navigation was consumed, so the in-flight guard clears here. Drains one batch and
// delivers via a single window.location.href navigation; remaining messages go through the GUARDED
// ping path (the old unguarded next-tick re-ping was itself a race: it could cancel the batch
// navigation it followed).
(window as unknown as Record<string, unknown>).__drainOutboxNavigate = (): void => {
  navInFlight = false;
  const drain = (window as unknown as { __drainOutbox: () => string }).__drainOutbox;
  const batch = drain();
  if (!batch) return;

  if (!viewReadyEgress.logged && batch.includes('"action":"viewReady"')) {
    viewReadyEgress.logged = true;
    const now = performance.now();
    const toPing = viewReadyEgress.pingAt < 0 ? 0 : viewReadyEgress.pingAt - viewReadyEgress.enqueuedAt;
    const pingToDrain = viewReadyEgress.pingAt < 0 ? now - viewReadyEgress.enqueuedAt : now - viewReadyEgress.pingAt;
    diagLog('Perf', `viewReady egress: enqueue→ping=${toPing.toFixed(1)} ping→drain=${pingToDrain.toFixed(1)} ` +
      `retries=${viewReadyEgress.retries} navInFlight=${viewReadyEgress.navInFlight}`);
  }

  // 20260908 - HELP-OPEN TIMING PROBE: decide BEFORE the navigate (the batch string is consumed
  // above and openHelpEgress is cleared here), but report AFTER — diagLog itself enqueues a bridge
  // message, and enqueuing one ahead of the openHelp navigation would put a competing navigation in
  // the very window this number measures. Reporting after the batch has left is free.
  let openHelpEgressReport: string | null = null;
  if (!openHelpEgress.logged && batch.includes('"action":"openHelp"')) {
    openHelpEgress.logged = true;
    const now = performance.now();
    const toPing = openHelpEgress.pingAt < 0 ? 0 : openHelpEgress.pingAt - openHelpEgress.enqueuedAt;
    const pingToDrain = openHelpEgress.pingAt < 0 ? now - openHelpEgress.enqueuedAt : now - openHelpEgress.pingAt;
    openHelpEgressReport = `openHelp egress: enqueue→ping=${toPing.toFixed(1)} ping→drain=${pingToDrain.toFixed(1)} ` +
      `retries=${openHelpEgress.retries} navInFlightAtEnqueue=${openHelpEgress.navInFlight} drainedAtEpoch=${Date.now()}`;
  }

  navigate(`previspro://bridge?msg=${encodeURIComponent(batch)}`);

  if (openHelpEgressReport) diagLog('HelpTrace', openHelpEgressReport);

  if (outbox.length > 0) schedulePing();
};

// Expose outbox length so native can wait for drain completion before team upload.
(window as unknown as Record<string, unknown>).__outboxLength = (): number => outbox.length;

// ─── Audio chunks still waiting in the outbox ────────────────────────────
//
// 20261005 - THE TEARDOWN DRAIN'S AUDIO. A clip's bytes go out as setAudioBlobChunk messages through this outbox
// (audioSyncBridge.ts sendChunked). A teardown drain that lands while some of them are still queued would lose them
// with the page, so the audio flush bundles exactly those clips (and nothing it already handed over) and then drops
// their queued chunks so the bytes are not written twice. sendToNative builds every queued string as
// JSON.stringify({ action, ...payload }) and sendChunked's payload starts with clipId, so a chunk always starts with
// this prefix followed by the id: a prefix test on strings that can be 700 KB each, never a JSON.parse.
const AUDIO_BLOB_CHUNK_PREFIX = '{"action":"setAudioBlobChunk","clipId":';

function audioBlobChunkClipIdOf(msg: string): number | null {
  if (!msg.startsWith(AUDIO_BLOB_CHUNK_PREFIX)) return null;
  let end = AUDIO_BLOB_CHUNK_PREFIX.length;
  if (msg.charCodeAt(end) === 45 /* - */) end++;
  const digitsStart = end;
  while (end < msg.length) {
    const code = msg.charCodeAt(end);
    if (code < 48 || code > 57) break;
    end++;
  }
  if (end === digitsStart) return null; // not a number (never produced by sendChunked)
  const id = Number(msg.slice(AUDIO_BLOB_CHUNK_PREFIX.length, end));
  return Number.isInteger(id) ? id : null;
}

/** The clip ids that have setAudioBlobChunk messages waiting in the outbox (not yet drained to native). */
export function outboxAudioBlobChunkClipIds(): Set<number> {
  const ids = new Set<number>();
  for (const msg of outbox) {
    const id = audioBlobChunkClipIdOf(msg);
    if (id !== null) ids.add(id);
  }
  return ids;
}

/**
 * Remove the waiting setAudioBlobChunk messages of these clips from the outbox, keeping every other message in its
 * order. Returns how many were removed. For a clip whose bytes now travel whole in the teardown bundle instead.
 */
export function dropOutboxAudioBlobChunks(clipIds: ReadonlySet<number>): number {
  let kept = 0;
  for (let read = 0; read < outbox.length; read++) {
    const msg = outbox[read];
    const id = audioBlobChunkClipIdOf(msg);
    if (id !== null && clipIds.has(id)) continue;
    outbox[kept] = msg;
    kept++;
  }
  const dropped = outbox.length - kept;
  outbox.length = kept;
  return dropped;
}

// Collect all stashed force-flush payloads (audio bundle + bridge messages) and send
// as a single combined navigation. Called by C# after all __flush*Sync() calls complete.
// This avoids WKWebView's silent-drop bug where multiple synchronous location.href
// assignments lose all but the last.
// 20261005 - Through navigate(), so the bundle registers itself in flight like every other navigation: a ping
// scheduled in the same tick (anything enqueued just before or after the drain) now DEFERS behind it instead of
// navigating over it, until native acknowledges the bundle (its __drainOutboxNavigate) or the in-flight timeout.
// The bare location.href it used before was outside the guard, so such a ping could cancel the bundle exactly the
// way the old bundle-then-sentinel pair did (TimelineWebViewPanel.BoardFlushDrainScript).
(window as unknown as Record<string, unknown>).__collectAndSendForceFlush = (): void => {
  const win = window as unknown as Record<string, unknown>;
  const audioBundleJson = win.__pendingForceFlushAudio as string | undefined;
  const messages = win.__pendingForceFlushMessages as string[] | undefined;

  // Clear stashes
  win.__pendingForceFlushAudio = undefined;
  win.__pendingForceFlushMessages = [];

  const hasAudio = !!audioBundleJson;
  const hasMessages = Array.isArray(messages) && messages.length > 0;

  if (!hasAudio && !hasMessages) return;

  const bundle = JSON.stringify({
    action: 'forceFlushBundle',
    audio: audioBundleJson ? JSON.parse(audioBundleJson) : null,
    messages: hasMessages ? messages : [],
  });

  navigate(`previspro://bridge?msg=${encodeURIComponent(bundle)}`);
};

// 20261005 - The teardown drain's sentinel, the drain's one navigation when nothing was stashed. Through navigate()
// for the same reason as the bundle above: registered in flight, so nothing the page enqueues next can cancel it
// before native intercepts it. The drain scripts (TimelineWebViewPanel.BoardFlushDrainScript,
// ScreenplayWebViewController.CoverageRetireDrainScript) call this when it is a function and otherwise assign the same
// URL themselves (a page whose bundle predates this function).
(window as unknown as Record<string, unknown>).__sendFlushDoneSentinel = (): void => {
  navigate('previspro://bridge?msg=__flushDone__');
};

// ─── Diagnostic logging ──────────────────────────────────────────────────

/** Diagnostic log that appears in both browser console AND Xcode (via bridge). */
export function diagLog(tag: string, msg: string): void {
  console.log(`[${tag}] ${msg}`);
  if (capabilities.bridge) {
    sendToNative('diagLog', { tag, msg });
  }
}
